import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/Addons.js'
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib.js";
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { Sky } from 'three/examples/jsm/objects/Sky.js'

/**
 * Base
 */
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()


/**
 * Lights
 */
RectAreaLightUniformsLib.init();

// Remove/disable strong ambient
// (keep a tiny ambient if you want)
const ambient = new THREE.AmbientLight(0xffffff, 1);
scene.add(ambient);

const directionalLight = new THREE.DirectionalLight(0xffffff, 4);
directionalLight.position.set(0, 5, 5);
directionalLight.castShadow = true;
directionalLight.shadow.mapSize.set(1024, 1024);
directionalLight.shadow.camera.far = 15;
directionalLight.shadow.camera.left = -7;
directionalLight.shadow.camera.top = 7;
directionalLight.shadow.camera.right = 7;
directionalLight.shadow.camera.bottom = -7;
scene.add(directionalLight);


// Add environment map for lighting (reflections and ambient lighting)
const rgbeLoader = new RGBELoader()
rgbeLoader.load('./textures/environmentMap/6k.hdr', (environmentMap) => {
    environmentMap.mapping = THREE.EquirectangularReflectionMapping
    scene.environment = environmentMap
})

/**
 * Models and Loading
 */
let model // Reference to the loaded model
let currentModelIndex = 0 // Track the current model index for interactions
let currentLoadId = 0 // Track loading request to prevent race conditions
let initialRotationX = 0
let initialRotationY = 0

// Model configurations
const models = [
    { path: './model/model.glb', lottiePath: './loader/loader.json' },
    { path: './model/dices.glb', lottiePath: './loader/data.json' },
    { path: './model/smile.glb', lottiePath: './loader/file.json' }
]

// GLTF Loader setup
const modelLoader = new GLTFLoader()
modelLoader.setMeshoptDecoder(MeshoptDecoder)

// Function to load a model and update buttons
const loadModel = (modelIndex, targetBtn, otherBtns) => {
    const loadId = ++currentLoadId;
    // Remove current model
    if (model) {
        scene.remove(model)
        model = null
    }

    // Only update buttons if targetBtn is provided (i.e., not initial load)
    if (targetBtn) {
        // Disable the clicked button and enable the others
        targetBtn.disabled = true
        targetBtn.classList.add('is-disabled')
        targetBtn.classList.remove('is-active')

        otherBtns.forEach(btn => {
            btn.disabled = false
            btn.classList.add('is-active')
            btn.classList.remove('is-disabled')
        })
    }

    // Show and reinitialize Lottie loader
    const loaderDiv = document.getElementById('lottie-loader')
    loaderDiv.style.display = 'block'
    loaderDiv.classList.remove('fade-out')  // Ensure it's not faded
    const lottieLoader = lottie.loadAnimation({
        container: loaderDiv,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: models[modelIndex].lottiePath
    })

    // Load the new model
    modelLoader.load(
        models[modelIndex].path,
        (gltf) => {
            if (loadId !== currentLoadId) return; // Ignore if a new model was requested
            gltf.scene.scale.set(0, 0, 0)  // Start at scale 0 for zoom effect
            gltf.scene.position.set(0, -0.5, 0)
            gltf.scene.rotation.y = Math.PI * 1.5  // Initial Y rotation

            // Enable shadows for all meshes in the model
            gltf.scene.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            })

            scene.add(gltf.scene)

            model = gltf.scene // Store reference for parallax
            currentModelIndex = modelIndex // Update current model index
            initialRotationX = gltf.scene.rotation.x  // Store initial X (usually 0)
            initialRotationY = gltf.scene.rotation.y  // Store initial Y

            // Reset camera position and controls
            camera.position.set(0, 0, 4)
            controls.reset()

            // Animate scale to final size (1.5) over 1 second
            const targetScale = 1.5
            const duration = 1000  // ms
            const startTime = Date.now()
            const animateScale = () => {
                const elapsed = Date.now() - startTime
                const progress = Math.min(elapsed / duration, 1)
                const easeProgress = 1 - Math.pow(1 - progress, 3)  // Cubic ease-out
                const currentScale = targetScale * easeProgress
                gltf.scene.scale.set(currentScale, currentScale, currentScale)
                if (progress < 1) {
                    requestAnimationFrame(animateScale)
                }
            }
            animateScale()

            // Add temporary camera auto-rotation on load
            controls.autoRotate = true
            controls.autoRotateSpeed = 0.5  // Adjust speed as needed (positive for clockwise)
            setTimeout(() => {
                controls.autoRotate = false
            }, 2000)  // Rotate for 5 seconds, then stop

            // Fade out the Lottie loader
            loaderDiv.classList.add('fade-out')
            setTimeout(() => {
                lottieLoader.destroy()  // Clean up after fade
                loaderDiv.style.display = 'none'  // Hide the div to prevent event blocking
            }, 500)  // Match the transition duration
        }
    )
}

// Initial model load (using the first model)
loadModel(0, null, [])  // No buttons to update initially

/**
 * Mouse parallax & Interaction
 */
const raycaster = new THREE.Raycaster()
const mouse = { x: 0, y: 0 }

window.addEventListener('mousemove', (event) => {
    mouse.x = (event.clientX / sizes.width) * 2 - 1
    mouse.y = -(event.clientY / sizes.height) * 2 + 1
})

window.addEventListener('click', () => {
    if (currentModelIndex === 2 && model) {
        raycaster.setFromCamera(mouse, camera)
        const intersects = raycaster.intersectObjects(model.children, true)

        if (intersects.length > 0) {
            const object = intersects[0].object
            // Only interact with meshes
            if (object.isMesh) {
                // Change material color to a random color
                object.material = object.material.clone() // Clone to ensure we don't affect shared materials
                object.material.color.setHex(Math.random() * 0xffffff)

                // Add a GSAP-like animation or just log it
                console.log('Clicked on:', object.name)
            }
        }
    }
})

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () => {
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 0
camera.position.y = 0
camera.position.z = 4
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

// Add zoom limits
controls.minDistance = 2  // Minimum zoom distance
controls.maxDistance = 10  // Maximum zoom distance

controls.enablePan = false

// Add drag limits (rotation angles)
controls.minPolarAngle = 0  // Minimum vertical angle (radians, 0 = top)
controls.maxPolarAngle = Math.PI  // Maximum vertical angle (radians, PI = bottom)

/**
 * Sky
 */

const sky = new Sky()
sky.scale.set(100, 100, 100)
scene.add(sky)

sky.material.uniforms['turbidity'].value = 10
sky.material.uniforms['rayleigh'].value = 3
sky.material.uniforms['mieCoefficient'].value = 0.1
sky.material.uniforms['mieDirectionalG'].value = 0.95
sky.material.uniforms['sunPosition'].value.set(0.3, -0.038, -0.95)

/**
 * Fog
 */

scene.fog = new THREE.FogExp2('#ffffff', 0.1)

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1
renderer.outputColorSpace = THREE.SRGBColorSpace

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () => {
    const elapsedTime = clock.getElapsedTime()

    // Update model parallax rotation and floating animation
    if (model) {

        // Raycaster for cursor pointer
        if (currentModelIndex === 2) {
            raycaster.setFromCamera(mouse, camera)
            const intersects = raycaster.intersectObjects(model.children, true)
            if (intersects.length > 0) {
                document.body.style.cursor = 'pointer'
            } else {
                document.body.style.cursor = 'default'
            }
        } else {
            document.body.style.cursor = 'default'
        }

        model.rotation.y = initialRotationY + mouse.x * 0.05  // Add parallax to initial Y
        model.rotation.x = initialRotationX + mouse.y * 0.05  // Add parallax to initial X
        // Enhanced floating animation: slight Y bobbing and Z rotation
        model.position.y = -0.5 + Math.sin(elapsedTime * 1) * 0.05  // Increased amplitude for more noticeable float
        model.rotation.z = Math.sin(elapsedTime * 0.003) * 0.02  // Subtle Z-axis rotation for bobbing effect
    }

    // Update controls
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()

/**
 * Button interactions for model switching
 */
const model1Btn = document.getElementById('model1-btn')
const model2Btn = document.getElementById('model2-btn')
const model3Btn = document.getElementById('model3-btn')

// Event listeners for buttons
model1Btn.addEventListener('click', () => {
    loadModel(0, model1Btn, [model2Btn, model3Btn])
})

model2Btn.addEventListener('click', () => {
    loadModel(1, model2Btn, [model1Btn, model3Btn])
})

model3Btn.addEventListener('click', () => {
    loadModel(2, model3Btn, [model1Btn, model2Btn])
})

/**
 * Burger menu toggle
 */
const burgerMenu = document.getElementById('burger-menu')
const modelButtons = document.getElementById('model-buttons')

burgerMenu.addEventListener('click', () => {
    modelButtons.classList.toggle('open')
})